import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

import { BACKEND_URL } from "@/lib/backendUrl";
const API = `${BACKEND_URL}/api`;
const fmt = (n) => `S$${Number(n || 0).toLocaleString("en-SG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const d = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-SG", { day: "2-digit", month: "2-digit", year: "numeric" });

export function SoaEmailDialog({ open, onClose, soa, doc, shop, printer }) {
  const [f, setF] = useState({ to: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !soa) return;
    const period = `${d(soa.period.from)} – ${d(soa.period.to)}`;
    setF({ to: soa.customer.email || "", subject: `Statement of Account – ${soa.customer.company || soa.customer.name} – ${period}`,
      message: `Dear ${soa.customer.contact_person || soa.customer.name},\n\nPlease find attached your Statement of Account from ${shop.name} for the period ${period}.\n\nOpening balance: ${fmt(soa.opening_balance)}\nClosing balance: ${fmt(soa.closing_balance)}\nOutstanding balance: ${fmt(soa.outstanding)}${soa.overdue > 0 ? `\nOverdue: ${fmt(soa.overdue)}` : ""}\n\nKindly arrange payment for any outstanding amount by PayNow (UEN ${shop.paynowUen || "40069900A"}) or bank transfer, quoting the invoice number as reference.\n\nThank you for your business.\n${shop.name}` });
  }, [open, soa, shop]);
  const send = async () => {
    setBusy(true);
    try {
      const pdf = await printer.pdfBase64(doc);
      await axios.post(`${API}/customers/${soa.customer.id}/soa/email`, { ...f, pdf_base64: pdf.base64, filename: pdf.filename || doc.filename });
      toast.success(`Statement emailed to ${f.to}`); onClose();
    } catch (e) {
      const dt = e?.response?.data?.detail;
      const msg = typeof dt === "string" ? dt : Array.isArray(dt) ? dt.map((x) => x?.msg || JSON.stringify(x)).join("; ") : String(e?.message || e);
      toast.error("Email failed", { description: msg });
    }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#111827] border-slate-700 text-slate-100" data-testid="soa-email-dialog">
        <DialogHeader><DialogTitle className="font-heading text-lg">Email statement · 发送对账单</DialogTitle><DialogDescription className="text-slate-400">The SOA PDF is attached automatically. Edit the message before sending.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input value={f.to} readOnly data-testid="soa-email-to" className="h-9 bg-slate-950/60 border-slate-700 text-sm text-slate-300" placeholder="No email on the customer record" title="Statements are only sent to the address saved on the customer record" />
          <Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="soa-email-subject" className="h-9 bg-slate-950/60 border-slate-700 text-sm" />
          <Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={10} data-testid="soa-email-message" className="bg-slate-950/60 border-slate-700 text-sm" />
        </div>
        <Button onClick={send} disabled={busy || !f.to} data-testid="soa-email-send" className="w-full bg-sky-700 hover:bg-sky-600 text-white font-bold">{busy ? "Sending…" : "Send with PDF attached"}</Button>
      </DialogContent>
    </Dialog>
  );
}
